const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/common/branding/theme.ts';
import { colors } from '@cistec/common_web/defs/colors';
import { createTheme, ThemeOptions } from '@mui/material';
import { theme as defaultTheme } from 'App/utils/clientSetupManager';

/**
 * This is the theme object which should be used for material ui components and also non-material ui components
 * Source: https://material-ui.com/customization/default-theme/
 */

interface customTheme {
    [key: string]: customTheme | string | number;
}

export interface PatAppTheme extends ThemeOptions {
    custom?: customTheme;
    // MUI got wrong typedefinitions
    spacing?: any;
    typography?: any;
    palette?: any;
}

const themeRaw = createTheme();

export const theme: PatAppTheme = {
    breakpoints: {
        values: {
            xs: 480,
            sm: 600,
            md: 840,
            lg: 1280,
            xl: 1920,
        },
    },
    palette: {
        common: colors.common,
        primary: {
            main: colors.teal['500'],
        },
        secondary: {
            main: colors.teal['500'],
        },
        error: {
            main: colors.red.A400,
            contrastText: colors.common.white,
        },
        warning: {
            light: colors.orange.A200,
            main: colors.orange.A400,
            dark: colors.orange.A700,
        },
        info: {
            main: colors.blue.A400,
        },
        success: {
            main: colors.green.A400,
        },
        grey: { ...colors.grey },
        background: {
            // CARD
            paper: colors.grey['50'],
            // Background (real background-most background
            default: colors.teal['200'],
        },
    },
    components: {
        MuiCardContent: {
            styleOverrides: {
                root: {
                    backgroundColor: defaultTheme?.palette?.background?.paper,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                positionStart: { marginRight: 0 },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',

                    '& > span': {
                        cursor: 'pointer',
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    alignItems: 'flex-start',
                    display: 'flex',
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    padding: 0,
                    marginTop: themeRaw.spacing(1 / 8),
                    paddingRight: themeRaw.spacing(1),
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    marginTop: -themeRaw.spacing(1.2),
                    marginLeft: themeRaw.spacing(1 / 8),
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${colors.teal['500']} !important`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${colors.teal['500']} !important`,
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    // fix: unify all paddings
                    padding: '8px 16px 8px',
                    // wrap word in select list
                    whiteSpace: 'normal',
                },
            },
        },
        // this is necessary to wrap text in the dropdown list
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    whiteSpace: 'normal',
                },
            },
        },
    },
    custom: {
        gradientLoginPage: 'linear-gradient(135deg, #7dd27d 0%, #1eb9b4 100%)',
        progressNotDone: colors.teal['300'],
        shadowFooter: `0px -2px 4px -1px rgb(0 0 0 / 20%), 0px -4px 5px 0px rgb(0 0 0 / 14%), 0px -1px 10px 0px rgb(0 0 0 / 12%);`,
    },
    typography: {
        h1: {
            fontSize: '1.14rem',
            fontWeight: 500,
            color: '#212121',
        },
        h2: {
            fontSize: '1.8rem',
            color: '#212121',
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 400,
        },
        h6: {
            fontSize: '1.14rem',
        },
        subtitle1: {
            fontSize: '1.5rem',
            color: '#212121',
        },
        subtitle2: {
            color: '#212121',
            fontSize: '1rem',
            textTransform: 'uppercase',
        },
    },
};
